import React, { useState, useEffect } from "react";
import {
  Select,
  Option,
  Menu,
  MenuHandler,
  MenuList,
  MenuItem,
  Button,
} from "@material-tailwind/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { ArrowRightIcon } from "@heroicons/react/24/solid";
import { selectedCountries, usdCountries } from "../../utils/constants";
import { useCountries } from "use-react-countries";
import { isValidAmount } from "../../utils/helpers";
import { AlertCustomStyles } from "../layout/AlertCustomStyles";
import { useNavigate } from "react-router-dom";
import ConversionRateService from "../../services/conversionRate.service";
import { useTranslation } from "react-i18next";

const TransferForm = ({ type, amtData, handleFormContinue }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Fetch countries and sort them
  const countries = useCountries()
    .countries.filter((x) => selectedCountries.includes(x.name))
    .sort((a, b) => a.name.localeCompare(b.name));

  const [alert, setAlert] = useState("");
  const [rate, setRate] = useState("");
  const [USDRate, setUSDRate] = useState("");
  const [discountedFee, setDiscountedFee] = useState("");
  const [originalFee, setOriginalFee] = useState("");
  const [countryPerHundredFee, setCountryPerHundredFee] = useState(0);
  const [openMenu, setOpenMenu] = React.useState(false);
  const [recipientCurrency, setRecipeintCurrency] = React.useState("");
  const [selectedCountryName, setSelectedCountryName] = React.useState("");

  const [values, setValues] = useState({
    /* rCountry: countries[0]?.name || "",
    sAmount: 100,
    rAmount: "",
    rCurrency: "NKF",
    sCurrency: "CAD",
    totalPay: 0, */
    rCountry: amtData.rCountry || countries[0].name,
    sAmount: amtData.sAmount || 100,
    rAmount: amtData.rAmount || "",
    rCurrency: amtData.rCurrency || "NKF",
    sCurrency: "CAD",
    totalPay: amtData.totalPay || 0,
  });

  const [errors, setErrors] = useState({
    sAmount: 0,
    rAmount: 0,
  });
  const handleCurrencyChange = async (currency) => {
    setRecipeintCurrency(currency);
    await fetchRealTimeRateSAmountChange(
      values.rCountry,
      values.sAmount,
      currency
    );
  };
  const calculateTierBasedFee = (amount, feeRate) => {
    const roundedAmount = Math.round(amount / 50) * 50;

    let discount = 0;

    // Define fee tiers based on amount
    if (amount > 1000) {
      discount = 0; // 10% discount for amounts over 1000
    } else if (amount > 500) {
      discount = 0; // 5% discount for amounts between 501 and 1000
    } else {
      discount = 0; // No discount for amounts up to 500
    }
    const originalFee = roundedAmount * parseFloat(feeRate);
    const discountedFee = Math.round(originalFee * (1 - discount)); // Apply discount
    setOriginalFee(originalFee);
    setDiscountedFee(discountedFee);
    return Math.round(discountedFee); // Return the rounded fee
  };

  const handleValidation = (e) => {
    if (e.target.name == "sAmount" || e.target.name == "rAmount") {
      setErrors({
        ...errors,
        [e.target.name]: !isValidAmount(e.target.value) ? 1 : 0,
      });
    }
  };
  const fetchRealTimeRateRAmountChange = async (
    countryName,
    recipientAmount,
    recipientCurrency
  ) => {
    const rates = await ConversionRateService.getAllConversionRates();
    const USDRates = rates.find((x) => x.country === "USA");
    const fee = USDRates.fee;
    const rate = USDRates.rate;
    const recipientAmountValid = parseFloat(recipientAmount) || 0;
    setSelectedCountryName(countryName);
    // Convert the send amount (CAD) to USD based on the conversion rate and round it
    const sAmount = Math.round(
      parseFloat(recipientAmountValid) * parseFloat(rate)
    );

    // Calculate the fee for the amount in USD (get the fee for 100 USD in CAD and compute) and round it
    const feePerHundredCAD = parseFloat(fee) || 0; // The fee for 100 USD in CAD
    const totalFee = calculateTierBasedFee(
      recipientAmount,
      feePerHundredCAD / 100
    ); // Fee for the converted amount in USD

    // Calculate totalPay (CAD) = sAmount (CAD) + fee (CAD) and round it
    const totalPay = Math.round(parseFloat(sAmount) + totalFee);
    setDiscountedFee(totalFee);
    setRate(rate);
    // Set the recipient amount and totalPay, rounded to the nearest whole number
    setValues((prev) => ({
      ...prev,
      sAmount: sAmount,
      totalPay: totalPay,
    }));
  };
  const fetchRealTimeRateSAmountChange = async (
    countryName,
    senderAmount,
    recipientCurrency
  ) => {
    const rates = await ConversionRateService.getAllConversionRates();
    const selectedCountry = rates.find((x) => x.country === countryName);
    setSelectedCountryName(countryName);
    if (recipientCurrency == "USD") {
      // Get USD to CAD rate from ConversionRateService
      const USDRates = rates.find((x) => x.country === "USA");
      const fee = USDRates.fee || 0;
      const rate = USDRates.rate || 0;
      const sAmount = parseFloat(senderAmount) || 0;
      // Convert the send amount (CAD) to USD based on the conversion rate and round it
      const rAmount = Math.round(parseFloat(sAmount) / parseFloat(rate)) || 0;

      // Calculate the fee for the amount in USD (get the fee for 100 USD in CAD and compute) and round it
      const feePerHundredCAD = parseFloat(fee); // The fee for 100 USD in CAD
      const totalFee = calculateTierBasedFee(rAmount, feePerHundredCAD / 100); // Fee for the converted amount in USD

      // Calculate totalPay (CAD) = sAmount (CAD) + fee (CAD) and round it
      const totalPay = Math.round(parseFloat(sAmount) + totalFee);
      setDiscountedFee(totalFee);
      setRate(rate);
      // Set the recipient amount and totalPay, rounded to the nearest whole number
      setValues((prev) => ({
        ...prev,
        rAmount: rAmount || 0,
        totalPay: totalPay || 0,
      }));
    } else {
      // Non-USD currency logic
      setRate(selectedCountry.rate);
      setCountryPerHundredFee(selectedCountry.fee);
      const sAmount = parseFloat(senderAmount) || 0;
      // Calculate and round the total fee
      const totalFee = calculateTierBasedFee(
        parseFloat(sAmount),
        selectedCountry.fee / 100
      );

      setDiscountedFee(totalFee);
      // Calculate and round the recipient amount
      const recipientAmount =
        Math.round(parseFloat(sAmount) * parseFloat(selectedCountry.rate)) || 0;

      // Set values with rounded numbers
      setValues((prev) => ({
        ...prev,
        rAmount: recipientAmount || 0,
        totalPay: totalFee + Math.round(parseFloat(sAmount)) || 0,
      }));
    }
  };

  const fetchRealTimeRateCountryChange = async (countryName) => {
    const rates = await ConversionRateService.getAllConversionRates();
    const selectedCountry = rates.find((x) => x.country === countryName);
    setRate(selectedCountry.rate);
    setCountryPerHundredFee(selectedCountry.fee);
    setSelectedCountryName(countryName);
    const totalFee = calculateTierBasedFee(100, selectedCountry.fee / 100);

    const recipientAmount = 100 * parseFloat(selectedCountry.rate) || 0;
    setRecipeintCurrency(selectedCountry.symbol);
    setDiscountedFee(totalFee);
    setValues((prev) => ({
      ...prev,
      rCountry: countryName,
      rCurrency: selectedCountry.symbol,
      rAmount: recipientAmount,
      totalPay: totalFee + 100,
    }));
  };
  const fetchRealTimeRateCountryChangeUSD = async (countryName) => {
    const rates = await ConversionRateService.getAllConversionRates();
    const USDRates = rates.find((x) => x.country === "USA");
    const fee = USDRates.fee;
    const rate = USDRates.rate;

    // Convert the send amount (CAD) to USD based on the conversion rate and round it
    const sAmount = Math.round(100 * parseFloat(rate));
    const rAmount = 100;

    // Calculate the fee for the amount in USD (get the fee for 100 USD in CAD and compute) and round it
    const feePerHundredCAD = parseFloat(fee); // The fee for 100 USD in CAD
    const totalFee = calculateTierBasedFee(100, feePerHundredCAD / 100); // Fee for the converted amount in USD

    // Calculate totalPay (CAD) = sAmount (CAD) + fee (CAD) and round it
    const totalPay = Math.round(sAmount + totalFee);
    setDiscountedFee(totalFee);
    setRate(rate);
    setRecipeintCurrency("USD");
    setSelectedCountryName(countryName);
    // Set the recipient amount and totalPay, rounded to the nearest whole number
    setValues((prev) => ({
      ...prev,
      rCountry: countryName,
      sAmount: sAmount,
      rAmount: 100,
      totalPay: totalPay,
    }));
  };

  const onChange = async (e) => {
    const { name, value } = e.target;
    const newValues = { ...values, [name]: value };
    if (name == "sAmount") {
      setValues(newValues);
      await fetchRealTimeRateSAmountChange(
        values.rCountry,
        value,
        recipientCurrency
      );
    } // Fetch rate on amount change
    if (name == "rAmount") {
      setValues(newValues);
      await fetchRealTimeRateRAmountChange(
        values.rCountry,
        value,
        recipientCurrency
      );
    } // Fetch rate on amount change
  };

  const handleCountryChange = async (countryName) => {
    if (!usdCountries.includes(countryName))
      await fetchRealTimeRateCountryChange(countryName);
    // Fetch rate on country change
    else fetchRealTimeRateCountryChangeUSD(countryName);
  };

  const handleNextStep = () => {
    if (type == "homePage") {
      if (errors.sAmount === 0 && values.sAmount) {
        navigate("/login");
      } else {
        setAlert("fail");
      }
    } else {
      if (
        Object.values(errors).every((v) => v === 0) &&
        !Object.values(values).some((x) => x == "")
      ) {
        values.rate = rate;
        values.fee = discountedFee;
        values.rCurrency = recipientCurrency;
        handleFormContinue(values);
      } else {
        setAlert("fail");
      }
    }
  };

  useEffect(() => {
    (async () => {
      await fetchRealTimeRateCountryChange(values.rCountry); // Fetch rate on initial load
    })();
  }, []);
  return (
    <div
      className="calculator-sora m-auto w-full max-w-none rounded-xl text-[#F5EEF8]"
    >
      <form className="mx-auto w-full max-w-screen-lg font-sora">
        {alert && (
          <AlertCustomStyles status={alert} message={"Fill required fields!"} />
        )}
        <div className="mb-0 flex flex-col gap-3">
          <h6 className="m-0 text-[14px] font-medium leading-5 tracking-normal text-[#F5EEF8]">
            You are sending to
          </h6>
          <div className="w-full pt-1">
            <Select
              size="lg"
              label="Send to"
              className="!min-h-[48px] !border-[#684C75] !px-3 !py-2 !font-sora !text-[13px] !text-[#F5EEF8]"
              labelProps={{
                className:
                  "!font-sora !text-[11px] !text-[#D8C8E0] before:!border-[#684C75] after:!border-[#B13BD0]",
              }}
              menuProps={{
                className:
                  "!w-[calc(100vw-4.5rem)] sm:!w-[390px] !max-h-[250px] !overflow-y-auto !rounded-xl !border !border-[#684C75] !bg-[#241430] !p-1 !font-sora !text-[#F5EEF8] !shadow-[0_16px_40px_rgba(0,0,0,0.45)]",
              }}
              selected={(element) =>
                element &&
                React.cloneElement(element, {
                  disabled: true,
                  className:
                    "pointer-events-none flex items-center gap-2 px-0 font-sora text-[13px] opacity-100",
                })
              }
              value={values["rCountry"]}
              onChange={(e) => handleCountryChange(e)}
            >
              {countries.map(({ name, flags, currencies }) => (
                <Option
                  key={name}
                  value={name}
                  className="!flex !items-center !gap-2 !rounded-lg !bg-transparent !font-sora !text-[13px] !text-[#F5EEF8] hover:!bg-[#4A2C5E] focus:!bg-[#4A2C5E]"
                >
                  <img
                    src={flags.svg}
                    alt={name}
                    className="h-5 w-5 rounded-full object-cover"
                  />
                  {name}
                </Option>
              ))}
            </Select>
          </div>
          <div className="flex w-full flex-col">
            <div className="relative m-auto w-full rounded-[15px] bg-[#2E1A3A] p-3">
              <div className="flex flex-col gap-2.5">
                <div className="relative flex flex-col gap-2">
                  <h6 className="m-0 text-[13px] font-semibold leading-5 tracking-normal text-[#F5EEF8]">
                    You send
                  </h6>
                  <div className="relative flex w-full overflow-hidden rounded-[10px] border border-[#684C75] bg-[#241430]">
                      <input
                        type="text"
                        name="sAmount"
                        value={values["sAmount"]}
                        onBlur={handleValidation}
                        onChange={onChange}
                        aria-label="You send"
                        className="h-[46px] min-w-0 flex-1 bg-transparent px-3 text-[20px] font-semibold leading-none text-[#F5EEF8] outline-none placeholder:text-[#B5A2C0] focus:ring-1 focus:ring-inset focus:ring-[#B13BD0]"
                        aria-invalid={!!errors["sAmount"]}
                      />
                    <Menu placement="bottom-start">
                      <MenuHandler>
                        {/* <Button
                          ripple={false}
                          variant="text"
                          color="blue-gray"
                          className="flex cursor-default md:min-h-[30px] min-h-[30px] h-10 items-center gap-2 rounded-r-none border border-r-1 border-blue-gray-200 bg-blue-gray-500/10 pl-3 disabled:text-black"
                        >
                          CAD
                        </Button> */}
                        <Button
                          variant="text"
                          className="m-0 flex min-w-[62px] items-center justify-center rounded-none border-l border-[#684C75] bg-[#241430] px-3 py-0 font-sora text-[13px] font-normal capitalize tracking-normal text-[#F5EEF8] shadow-none hover:shadow-none"
                        >
                          CAD
                        </Button>
                      </MenuHandler>
                    </Menu>
                  </div>
                  {errors["sAmount"] == 1 && (
                    <span className="ml-1 text-[10px] font-semibold text-[#e53935]">
                      Send amount must be between 50.00 and 2999.00
                    </span>
                  )}
                </div>
                <div className="flex min-h-[38px] items-center justify-between rounded-[9px] bg-[#4A2C5E] px-3 text-[#E2C8F0]">
                  <div className="text-[12px]">Rate</div>
                  <div className="flex items-center text-[11px] font-medium text-[#E2C8F0] sm:text-[12px]">
                    <span>
                      1.00 {recipientCurrency == "USD" ? "USD" : "CAD"}{" "}
                    </span>
                    <ArrowRightIcon className="mx-1 h-3 w-4 opacity-80" />
                    <span>
                      {rate}{" "}
                      {recipientCurrency == "USD" ? "CAD" : values["rCurrency"]}
                    </span>
                  </div>
                </div>
                <div className="relative mb-0 flex flex-col gap-2">
                  <h6 className="m-0 text-[13px] font-semibold leading-5 tracking-normal text-[#F5EEF8]">
                    Recipient gets
                  </h6>
                  <div className="relative flex w-full overflow-hidden rounded-[10px] border border-[#684C75] bg-[#241430]">
                    <input
                      type="text"
                      name="rAmount"
                      disabled={recipientCurrency == "USD" ? false : true}
                      value={values["rAmount"].toLocaleString()}
                      onBlur={handleValidation}
                      onChange={onChange}
                      aria-label="Recipient gets"
                      aria-invalid={!!errors["rAmount"]}
                      className="h-[46px] min-w-0 flex-1 bg-transparent px-3 text-[20px] font-semibold leading-none text-[#F5EEF8] outline-none focus:ring-1 focus:ring-inset focus:ring-[#B13BD0] disabled:opacity-100"
                    />
                    <Menu
                      placement="bottom-end"
                      open={openMenu}
                      handler={setOpenMenu}
                    >
                      <MenuHandler>
                        <Button
                          variant="text"
                          className="m-0 flex min-w-[62px] items-center justify-center rounded-none border-l border-[#684C75] bg-[#241430] px-3 py-0 font-sora text-[13px] font-normal capitalize tracking-normal text-[#F5EEF8] shadow-none hover:shadow-none"
                          onClick={() =>
                            usdCountries.includes(selectedCountryName)
                              ? ""
                              : setOpenMenu(!openMenu)
                          } // Toggle menu on click
                        >
                          {usdCountries.includes(selectedCountryName)
                            ? "USD"
                            : recipientCurrency}
                          {usdCountries.includes(selectedCountryName) ? (
                            ""
                          ) : (
                            <ChevronDownIcon
                              strokeWidth={2.5}
                              className={`h-3.5 w-3.5 transition-transform duration-200 ${
                                openMenu ? "rotate-180" : ""
                              }`}
                            />
                          )}
                        </Button>
                      </MenuHandler>
                      {!usdCountries.includes(selectedCountryName) && (
                        <MenuList className="!z-[1000] !min-w-[88px] !rounded-lg !border !border-[#684C75] !bg-[#241430] !p-1 !font-sora !text-[#F5EEF8] !shadow-[0_16px_40px_rgba(0,0,0,0.45)]">
                          <MenuItem
                            className="!rounded-md !font-sora !text-[12px] !text-[#F5EEF8] hover:!bg-[#4A2C5E] focus:!bg-[#4A2C5E]"
                            onClick={() =>
                              handleCurrencyChange(values["rCurrency"])
                            }
                          >
                            {values["rCurrency"]}
                          </MenuItem>
                          <MenuItem
                            className="!rounded-md !font-sora !text-[12px] !text-[#F5EEF8] hover:!bg-[#4A2C5E] focus:!bg-[#4A2C5E]"
                            onClick={() => handleCurrencyChange("USD")}
                          >
                            USD
                          </MenuItem>
                        </MenuList>
                      )}
                    </Menu>
                  </div>
                  {errors["rAmount"] == 1 && (
                    <span className="ml-1 text-[10px] font-semibold text-[#e53935]">
                      Recipient amount is not valid!
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="relative w-full px-0 text-[#D8C8E0]">
              <div className="mb-3 mt-1 flex flex-col gap-2.5 text-[12px]">
                <div className="flex items-center justify-between">
                  <span>Transfer amount</span>
                  <span className="text-[#F5EEF8]">{values["sAmount"] || 0} CAD</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>{t("fee")}</span>
                  <span className="text-[#F5EEF8]">{discountedFee} CAD</span>
                </div>
              </div>
              <hr className="border-[#684C75]" />
              <div className="flex items-center justify-between pt-3 text-[14px] font-semibold text-[#F5EEF8]">
                <span>{t("totalPay")}</span>
                <span>{values["totalPay"] || 0} CAD</span>
              </div>
            </div>
          </div>
        </div>
      </form>
      <div className="m-auto mt-4 w-full">
        <Button
          className="m-0 min-h-[44px] w-full rounded-full bg-[#B13BD0] px-4 py-2 font-sora text-[13px] font-semibold normal-case text-white shadow-none hover:bg-[#C351DD] hover:shadow-none"
          onClick={handleNextStep}
          size="sm"
        >
          {type == "homePage" ? t("getStarted") : "Continue"}
        </Button>
      </div>
    </div>
  );
};
export default TransferForm;
